import React, { useState } from 'react';
import { X, Clock, User, Calendar } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
  behavioral_tags?: {
    aggressive?: boolean;
    fearful?: boolean;
    go_slow?: boolean;
    caution?: boolean;
    demo?: boolean;
    notes?: string;
  };
}

interface Owner {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
}

interface Appointment {
  id: string;
  title: string;
  appointment_date: string;
  duration_minutes: number;
  appointment_type: string;
  status: string;
  assigned_vet_user_id?: string;
  description?: string;
  notes?: string;
  pets?: Pet[];
  pet_owner?: Owner;
}

interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

interface WaitlistCheckInModalProps {
  appointment: Appointment;
  teamMembers: TeamMember[];
  onClose: () => void;
  onSave: () => Promise<void>;
}

const BEHAVIORAL_TAG_COLORS = {
  aggressive: 'bg-orange-100 text-orange-800 border-2 border-orange-300',
  fearful: 'bg-blue-100 text-blue-800 border-2 border-blue-300',
  go_slow: 'bg-green-100 text-green-800 border-2 border-green-300',
  caution: 'bg-orange-100 text-orange-800 border-2 border-orange-300',
  demo: 'bg-blue-100 text-blue-800 border-2 border-blue-300',
};

export const WaitlistCheckInModal: React.FC<WaitlistCheckInModalProps> = ({
  appointment,
  teamMembers,
  onClose,
  onSave,
}) => {
  const [selectedVetId, setSelectedVetId] = useState<string>(appointment.assigned_vet_user_id || '');
  const [appointmentTime, setAppointmentTime] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, true);

  // Filter to only show vets (VET_STAFF and PRACTICE_ADMIN)
  const vetTeamMembers = teamMembers.filter(
    (member) => member.role === 'VET_STAFF' || member.role === 'PRACTICE_ADMIN'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedVetId) {
      setError('Please select a veterinarian');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      
      // Update appointment with vet and time
      const response = await fetch(`${API_BASE_URL}/api/v1/appointments/${appointment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          assigned_vet_user_id: selectedVetId,
          appointment_date: new Date(appointmentTime).toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update appointment');
      }

      await onSave();
      onClose();
    } catch (err) {
      console.error('Error checking in from waitlist:', err);
      setError(err instanceof Error ? err.message : 'Failed to check in appointment');
    } finally {
      setLoading(false);
    }
  };

  const pet = appointment.pets?.[0];
  const owner = appointment.pet_owner;

  const getActiveTags = (pet: Pet) => {
    const tags = [];
    if (pet.behavioral_tags?.aggressive) tags.push({ key: 'aggressive', label: 'Aggressive' });
    if (pet.behavioral_tags?.fearful) tags.push({ key: 'fearful', label: 'Fearful' });
    if (pet.behavioral_tags?.go_slow) tags.push({ key: 'go_slow', label: 'Go Slow' });
    if (pet.behavioral_tags?.caution) tags.push({ key: 'caution', label: 'Caution' });
    if (pet.behavioral_tags?.demo) tags.push({ key: 'demo', label: 'Demo' });
    return tags;
  };

  const activeTags = pet ? getActiveTags(pet) : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[500] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-gray-900 mb-1">
              Check In from Waitlist
            </h2>
            <p className="text-sm text-gray-500">Assign veterinarian and appointment time</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 ml-4"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Appointment Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Appointment Time <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Duration: {appointment.duration_minutes} minutes
            </p>
          </div>

          {/* Assign Veterinarian */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Assign to Veterinarian <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedVetId}
              onChange={(e) => setSelectedVetId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              required
            >
              <option value="">Select a veterinarian...</option>
              {vetTeamMembers.map((vet) => (
                <option key={vet.id} value={vet.id}>
                  {vet.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Patient Info */}
          {pet && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Patient Information
                </h3>
              </div>
              <div className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 text-lg font-bold flex-shrink-0">
                    {pet.name[0]}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{pet.name}</h3>
                    <p className="text-sm text-gray-600">
                      {pet.species} {pet.breed && `• ${pet.breed}`}
                    </p>
                  </div>
                </div>

                {/* Behavioral Tags */}
                {activeTags.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Temperament
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {activeTags.map((tag) => (
                        <div
                          key={tag.key}
                          className={`px-4 py-2.5 text-sm font-bold rounded-lg ${
                            BEHAVIORAL_TAG_COLORS[tag.key as keyof typeof BEHAVIORAL_TAG_COLORS]
                          }`}
                        >
                          {tag.label.toUpperCase()}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special Instructions */}
                {pet.behavioral_tags?.notes && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Special Instructions
                    </p>
                    <p className="text-sm text-gray-700 italic">
                      "{pet.behavioral_tags.notes}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Owner Info */}
          {owner && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Owner Information
                </h3>
              </div>
              <div className="p-4">
                <div className="text-sm">
                  <div className="font-medium text-gray-900 mb-1">{owner.full_name}</div>
                  {owner.phone && (
                    <div className="text-gray-600">{owner.phone}</div>
                  )}
                  {owner.email && (
                    <div className="text-gray-600">{owner.email}</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Appointment Details */}
          {(appointment.description || appointment.notes) && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="border-b border-gray-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Visit Notes
                </h3>
              </div>
              <div className="p-4 space-y-2">
                {appointment.description && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Reason for Visit
                    </p>
                    <p className="text-sm text-gray-900">{appointment.description}</p>
                  </div>
                )}
                {appointment.notes && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Internal Notes
                    </p>
                    <p className="text-sm text-gray-600">{appointment.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !selectedVetId}
          >
            {loading ? 'Checking In...' : 'Check In'}
          </button>
        </div>
      </div>
    </div>
  );
};

